/**
 * Collections Block JavaScript
 * Uses GSAP Draggable and InertiaPlugin for smooth sliding gallery
 */

import { isBlockOnPage, whenVisible } from "../../src/js/modules/block-present.js";

(function () {
  "use strict";

  const COLLECTION_BLOCKS = [
    "collections",
    "what-we-do",
    "other-projects",
    "team-slider",
    "custom-text-image-slider",
    "other-articles",
  ];

  // Wait for GSAP and plugins to be available
  function initCollections() {
    // Check if GSAP and required plugins are loaded
    if (
      typeof gsap === "undefined" ||
      typeof Draggable === "undefined" ||
      typeof InertiaPlugin === "undefined"
    ) {
      return;
    }

    // Register plugins
    gsap.registerPlugin(Draggable, InertiaPlugin);

    // Support collections, what-we-do, other-projects, team slider, gallery slider, custom text/image slider and other articles blocks
    const blocks = document.querySelectorAll(
      ".collections, .what-we-do, .other-projects, .team-slider, .gallery-slider, .custom-text-image-slider, .other-articles",
    );

    blocks.forEach((block) => {
      const container =
        block.querySelector(".collections-container") ||
        block.querySelector(".what-we-do-container") ||
        block.querySelector(".other-projects-container") ||
        block.querySelector(".other-articles-container");
      if (!container) return;

      new DraggableSlider(container);
    });
  }

  class DraggableSlider {
    constructor(container) {
      this.container = container;
      this.block = container.closest(
        ".collections, .what-we-do, .other-projects, .team-slider, .gallery-slider, .custom-text-image-slider, .other-articles",
      );
      const isOtherArticles = container.classList.contains(
        "other-articles-container",
      );
      const isWhatWeDo = container.classList.contains("what-we-do-container");
      const isOtherProjects = container.classList.contains(
        "other-projects-container",
      );
      const isOtherBlock = isOtherArticles;

      const guideSelector = isOtherBlock
        ? ".other-articles-guide"
        : isWhatWeDo
          ? ".what-we-do-guide"
          : isOtherProjects
            ? ".other-projects-guide"
            : ".collections-guide";
      const sliderSelector = isOtherBlock
        ? ".other-articles-slider"
        : isWhatWeDo
          ? ".what-we-do-slider"
          : isOtherProjects
            ? ".other-projects-slider"
            : ".collections-slider";
      const itemSelector = isOtherBlock
        ? ".other-articles-item"
        : isWhatWeDo
          ? ".what-we-do-item"
          : isOtherProjects
            ? ".other-projects-item"
            : ".collections-item";
      const cursorSelector = isOtherBlock
        ? ".other-articles-cursor"
        : ".collections-cursor";

      this.guide = container.querySelector(guideSelector);
      this.slider = container.querySelector(sliderSelector);
      this.cursor = container.querySelector(cursorSelector);
      this.overlaySelector = isOtherBlock
        ? ".article-card .link"
        : isWhatWeDo
          ? ".what-we-do-item .image_link"
          : isOtherProjects
            ? ".other-projects-item > a"
            : ".collections-overlay";
      this.isTeamSlider =
        this.block && this.block.classList.contains("team-slider");
      this.isCustomTextImageSlider =
        this.block &&
        this.block.classList.contains("custom-text-image-slider");
      this.isGallerySlider =
        this.block && this.block.classList.contains("gallery-slider");
      this.isCollections =
        this.block && this.block.classList.contains("collections");
      this.isWhatWeDo = isWhatWeDo;
      this.isOtherProjects = isOtherProjects;
      this.stackOnMobile =
        this.block && this.block.classList.contains("stack-on-mobile");
      this.slideItemSelector = itemSelector;
      this.carouselMql = null;
      if (this.isWhatWeDo) {
        this.carouselMql = window.matchMedia("(max-width: 1199px)");
        this.carouselMql.addEventListener("change", () =>
          this.syncWhatWeDoCarousel(),
        );
      } else if (this.isOtherProjects) {
        this.carouselMql = window.matchMedia("(max-width: 767px)");
        this.carouselMql.addEventListener("change", () =>
          this.syncWhatWeDoCarousel(),
        );
      } else if (this.isCustomTextImageSlider && this.stackOnMobile) {
        this.carouselMql = window.matchMedia("(max-width: 767px)");
        this.carouselMql.addEventListener("change", () =>
          this.syncStackedMobileCarousel(),
        );
      }
      this.refreshSlides();
      this.useMobilePagination =
        (this.isTeamSlider ||
          this.isCustomTextImageSlider ||
          this.isCollections ||
          (this.block &&
            (this.block.classList.contains("what-we-do") ||
              this.block.classList.contains("other-projects") ||
              this.block.classList.contains("gallery-slider")))) &&
        this.block;
      this.paginationDotClass =
        this.isWhatWeDo || this.isOtherProjects
          ? "slider-pagination__dot"
          : "team-slider-pagination__dot";
      this.paginationEl = this.block
        ? this.block.querySelector(
            this.isWhatWeDo || this.isOtherProjects
              ? ".slider-pagination"
              : ".team-slider-pagination",
          )
        : null;
      this.teamPaginationMql = null;
      this._teamPagRaf = null;
      this.slidesNumb = this.slides.length;
      this.sliderWidth = 0;
      this.dragMultiplier = 1.5; // Multiplier to amplify dragging (reduced for slower drag)
      this.maxDragX = 0;
      this.didDrag = false;
      this.dragThreshold = 5;
      this.dragStartX = 0;
      this.mouseX = 0;
      this.mouseY = 0;
      // Instant slider ↔ guide sync on small viewports avoids stacked 0.8s tweens fighting vertical page scroll.
      this.instantSliderMql = window.matchMedia(
        this.isWhatWeDo ? "(max-width: 1199px)" : "(max-width: 767px)",
      );

      if (!this.guide || !this.slider || this.slides.length === 0) {
        return;
      }

      window.addEventListener("resize", () => {
        if (this.isWhatWeDo || this.isOtherProjects) {
          this.syncWhatWeDoCarousel();
        } else if (this.isCustomTextImageSlider && this.stackOnMobile) {
          this.syncStackedMobileCarousel();
        } else {
          this.calculateBounds();
        }
      });

      this.init();
      this.initClickPrevent();
      if (this.cursor) this.initCursor();
      if (this.isGallerySlider) {
        this.bindSlideImageLoads();
      }
      if (this.useMobilePagination && this.paginationEl) {
        this.initTeamPagination();
      }
    }

    /**
     * Track width to scroll: flex scrollWidth can exceed the last slide's trailing edge
     * (extra empty track on mobile). Clamp to the last item, then add margin-right so a
     * trailing gutter is kept (scrollWidth often omits the last flex item's margin).
     */
    getSliderScrollExtent() {
      const scrollW = this.slider.scrollWidth;
      if (this.slides.length === 0) {
        return scrollW;
      }
      const last = this.slides[this.slides.length - 1];
      const mr = parseFloat(getComputedStyle(last).marginRight) || 0;
      const itemEnd = last.offsetLeft + last.offsetWidth;
      return Math.min(scrollW, itemEnd) + mr;
    }

    refreshSlides() {
      this.slides = [...this.container.querySelectorAll(this.slideItemSelector)];
      this.slidesNumb = this.slides.length;
    }

    bindSlideImageLoads() {
      if (!this.slider) {
        return;
      }
      const onLoad = () => this.calculateBounds();
      this.slider.querySelectorAll("img").forEach((img) => {
        if (img.complete) {
          return;
        }
        img.addEventListener("load", onLoad, { once: true });
      });
    }

    isStackedMobile() {
      return (
        this.stackOnMobile && window.matchMedia("(max-width: 767px)").matches
      );
    }

    syncWhatWeDoCarousel() {
      if (
        (!this.isWhatWeDo && !this.isOtherProjects) ||
        !this.carouselMql
      ) {
        return;
      }
      this.refreshSlides();
      const useCarousel = this.carouselMql.matches && !this.isStackedMobile();
      if (useCarousel) {
        this.calculateBounds();
        if (!this.draggable) {
          this.createDraggable();
        }
      } else {
        if (this.draggable) {
          this.draggable.kill();
          this.draggable = null;
        }
        gsap.killTweensOf([this.guide, this.slider]);
        gsap.set(this.slider, { clearProps: "width,transform" });
        gsap.set(this.guide, { clearProps: "width,transform" });
      }
      if (this.useMobilePagination && this.paginationEl) {
        this.syncTeamPaginationUi();
      }
    }

    syncStackedMobileCarousel() {
      if (
        !this.isCustomTextImageSlider ||
        !this.stackOnMobile ||
        !this.carouselMql
      ) {
        return;
      }
      this.refreshSlides();
      const mobile = this.carouselMql.matches;
      if (mobile) {
        if (this.draggable) {
          this.draggable.kill();
          this.draggable = null;
        }
        gsap.killTweensOf([this.guide, this.slider]);
        gsap.set(this.slider, { clearProps: "width,transform" });
        gsap.set(this.guide, { clearProps: "width,transform" });
      } else {
        this.calculateBounds();
        if (!this.draggable) {
          this.createDraggable();
        }
      }
      if (this.useMobilePagination && this.paginationEl) {
        this.syncTeamPaginationUi();
      }
    }

    calculateBounds() {
      this.refreshSlides();
      const viewportWidth = this.container.offsetWidth;

      const totalWidth = this.getSliderScrollExtent();
      this.sliderWidth = totalWidth;

      // Max drag: last slide's trailing edge (including last-item margin) vs viewport
      const maxSliderX = -(totalWidth - viewportWidth);
      this.maxDragX = maxSliderX / this.dragMultiplier;

      // Ensure we don't allow dragging if content fits in viewport
      if (totalWidth <= viewportWidth) {
        this.maxDragX = 0;
      }

      // Keep the current offset when images load / the viewport resizes.
      // Resetting to 0 made the gallery jump back to the start mid-drag.
      const currentGuideX = this.draggable
        ? this.draggable.x
        : parseFloat(gsap.getProperty(this.guide, "x")) || 0;
      const clampedGuideX = Math.max(
        this.maxDragX,
        Math.min(0, currentGuideX),
      );

      gsap.set(this.slider, {
        width: totalWidth,
        x: clampedGuideX * this.dragMultiplier,
      });

      gsap.set(this.guide, {
        width: totalWidth,
        x: clampedGuideX,
      });

      if (this.draggable) {
        this.draggable.applyBounds({ minX: this.maxDragX, maxX: 0 });
        this.draggable.update();
      }

      if (this.useMobilePagination && this.paginationEl) {
        this.syncTeamPaginationUi();
      }
    }

    initTeamPagination() {
      this.teamPaginationMql = window.matchMedia(
        this.isWhatWeDo ? "(max-width: 1199px)" : "(max-width: 767px)",
      );
      const onMq = () => this.syncTeamPaginationUi();
      this.teamPaginationMql.addEventListener("change", onMq);
      this.syncTeamPaginationUi();
    }

    syncTeamPaginationUi() {
      if (!this.paginationEl || !this.teamPaginationMql) {
        return;
      }
      const show =
        this.teamPaginationMql.matches &&
        this.slides.length > 1 &&
        this.sliderWidth > this.container.offsetWidth &&
        !this.isStackedMobile();
      if (show) {
        this.paginationEl.hidden = false;
        this.paginationEl.classList.add("is-visible");
        this.buildTeamPaginationDots();
        this.updateTeamPaginationActive();
      } else {
        this.paginationEl.hidden = true;
        this.paginationEl.classList.remove("is-visible");
        this.paginationEl.replaceChildren();
      }
    }

    buildTeamPaginationDots() {
      if (!this.paginationEl) {
        return;
      }
      const existing = this.paginationEl.querySelectorAll(
        `.${this.paginationDotClass}`,
      ).length;
      if (existing === this.slides.length) {
        return;
      }
      this.paginationEl.replaceChildren();
      this.slides.forEach((_, i) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = this.paginationDotClass;
        btn.setAttribute("aria-label", `Slide ${i + 1} of ${this.slides.length}`);
        btn.addEventListener("click", () => this.goToTeamSlide(i));
        this.paginationEl.appendChild(btn);
      });
    }

    getTeamActiveSlideIndex() {
      const x = parseFloat(gsap.getProperty(this.slider, "x")) || 0;
      let best = 0;
      let bestDist = Infinity;
      this.slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft + x);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    scheduleTeamPaginationActiveUpdate() {
      if (!this.paginationEl || !this.teamPaginationMql?.matches) {
        return;
      }
      if (this._teamPagRaf != null) {
        return;
      }
      this._teamPagRaf = requestAnimationFrame(() => {
        this._teamPagRaf = null;
        this.updateTeamPaginationActive();
      });
    }

    updateTeamPaginationActive(index) {
      if (!this.paginationEl || !this.paginationEl.classList.contains("is-visible")) {
        return;
      }
      const i = index === undefined ? this.getTeamActiveSlideIndex() : index;
      const dots = this.paginationEl.querySelectorAll(
        `.${this.paginationDotClass}`,
      );
      dots.forEach((dot, di) => {
        const on = di === i;
        dot.classList.toggle("is-active", on);
        if (on) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    }

    /** Guide-space snap targets (per slide); matches goToTeamSlide / posts-slider. */
    getSnapGuideXs() {
      if (this.slides.length === 0) {
        return [0];
      }
      const maxS = this.maxDragX * this.dragMultiplier;
      return this.slides.map((slide) => {
        let targetSliderX = -slide.offsetLeft;
        targetSliderX = Math.max(maxS, Math.min(0, targetSliderX));
        return targetSliderX / this.dragMultiplier;
      });
    }

    goToTeamSlide(index) {
      if (index < 0 || index >= this.slides.length) {
        return;
      }
      const slide = this.slides[index];
      let targetSliderX = -slide.offsetLeft;
      const maxS = this.maxDragX * this.dragMultiplier;
      targetSliderX = Math.max(maxS, Math.min(0, targetSliderX));
      const targetGuideX = targetSliderX / this.dragMultiplier;

      gsap.killTweensOf([this.guide, this.slider]);
      gsap.to(this.guide, {
        x: targetGuideX,
        duration: 0.45,
        ease: "power2.out",
        onUpdate: () => {
          if (this.draggable) {
            this.draggable.update();
          }
        },
        onComplete: () => {
          if (this.draggable) {
            this.draggable.update();
          }
        },
      });
      gsap.to(this.slider, {
        x: targetSliderX,
        duration: 0.45,
        ease: "power2.out",
      });
      this.updateTeamPaginationActive(index);
    }

    createDraggable() {
      let slider = this.slider;
      let dragMultiplier = this.dragMultiplier;
      const self = this;

      const onTeamSwipeFinish = () => {
        setTimeout(() => {
          if (
            self.useMobilePagination &&
            self.paginationEl &&
            self.teamPaginationMql?.matches
          ) {
            self.updateTeamPaginationActive();
          }
        }, 0);
      };

      // Slider must track the guide 1:1 during drag/throw. A delayed tween on desktop
      // (previously 0.8s) left the slides catching up after release and felt broken.
      const syncSliderToGuide = (guideX) => {
        const newX = guideX * dragMultiplier;
        gsap.set(slider, { x: newX });
      };

      const draggableVars = {
        type: "x",
        trigger: this.container,
        edgeResistance: 0.75,
        bounds: { minX: this.maxDragX, maxX: 0 },
        // Higher threshold so vertical page scroll commits before horizontal drag nudges the guide (type "x" cannot use lockAxisOnTouchScroll).
        minimumMovement: 10,
        inertia: true,
        throwProps: true,
        liveSnap: false,
        snap: {
          x: function (endValue) {
            // Gallery slider snaps on all viewports; other carousels only on mobile.
            const allowSnap =
              self.isGallerySlider ||
              (self.useMobilePagination && self.instantSliderMql.matches);
            if (!allowSnap) {
              return endValue;
            }
            const points = self.getSnapGuideXs();
            if (points.length < 2) {
              return endValue;
            }
            return gsap.utils.snap(points, endValue);
          },
        },
        onDragStart: function () {
          self.didDrag = false;
          self.dragStartX = this.x;
          gsap.killTweensOf(slider);
        },
        onDrag: function () {
          if (Math.abs(this.x - self.dragStartX) > self.dragThreshold) {
            self.didDrag = true;
          }
          syncSliderToGuide(this.x);
          self.scheduleTeamPaginationActiveUpdate();
        },
        onThrowUpdate: function () {
          syncSliderToGuide(this.x);
          self.scheduleTeamPaginationActiveUpdate();
        },
        onDragEnd: function () {
          setTimeout(function () {
            self.didDrag = false;
          }, 0);
          onTeamSwipeFinish();
        },
        onThrowComplete: onTeamSwipeFinish,
      };

      if (this.isCustomTextImageSlider) {
        draggableVars.dragClickables = false;
      }
      // GSAP Draggable otherwise sets an inline grab/move cursor that fights
      // cursor: none and the custom .collections-cursor.
      if (this.cursor) {
        draggableVars.cursor = false;
      }

      this.draggable = Draggable.create(this.guide, draggableVars)[0];
    }

    initClickPrevent() {
      this.container.addEventListener(
        "click",
        (e) => {
          if (this.didDrag) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    }

    initCursor() {
      if (!this.cursor) return;

      const cursor = this.cursor;
      const container = this.container;

      if (this.isTeamSlider) {
        gsap.set(cursor, {
          xPercent: -50,
          yPercent: -50,
          left: -1000,
          top: -1000,
        });
      } else {
        gsap.set(cursor, { left: -1000, top: -1000 });
      }

      const cursorOverlaySelectors = [this.overlaySelector].filter(Boolean);
      if (this.isCustomTextImageSlider) {
        cursorOverlaySelectors.push(".custom-slide-content");
      }

      const cursorPos = (clientX, clientY) => {
        if (this.isTeamSlider) {
          const rect = container.getBoundingClientRect();
          return { left: clientX - rect.left, top: clientY - rect.top };
        }
        return { left: clientX, top: clientY };
      };

      const handleMouseMove = (e) => {
        if (e.pointerType !== "mouse") return;
        this.mouseX = e.clientX;
        this.mouseY = e.clientY;
        const under = document.elementFromPoint(e.clientX, e.clientY);
        const isOverOverlay =
          under &&
          cursorOverlaySelectors.some((sel) => sel && under.closest(sel));
        if (isOverOverlay) {
          cursor.classList.remove("is-visible");
        } else {
          cursor.classList.add("is-visible");
          gsap.to(cursor, {
            ...cursorPos(this.mouseX, this.mouseY),
            duration: 0.3,
            ease: "power2.out",
            overwrite: "auto",
          });
        }
      };

      let fauxCursorListenersActive = false;

      const teardownFauxCursor = () => {
        cursor.classList.remove("is-visible");
        container.style.cursor = "";
        document.removeEventListener("pointermove", handleMouseMove);
        fauxCursorListenersActive = false;
      };

      // Touch devices fire emulated mouseenter on tap with no matching
      // mouseleave, which left the fixed-position cursor stuck on screen.
      document.addEventListener(
        "pointerdown",
        (e) => {
          if (e.pointerType !== "mouse" && fauxCursorListenersActive) {
            teardownFauxCursor();
          }
        },
        { passive: true }
      );

      if (this.isCustomTextImageSlider) {
        this.cursorDesktopMql = window.matchMedia("(min-width: 768px)");
        this.cursorDesktopMql.addEventListener("change", () => {
          if (!this.cursorDesktopMql.matches) {
            teardownFauxCursor();
          }
        });
      }

      container.addEventListener("pointerenter", (e) => {
        if (e.pointerType !== "mouse") return;
        const allowFaux =
          !this.isCustomTextImageSlider ||
          window.matchMedia("(min-width: 768px)").matches;
        if (!allowFaux) return;

        this.mouseX = e.clientX;
        this.mouseY = e.clientY;
        const under = document.elementFromPoint(e.clientX, e.clientY);
        const isOverOverlay =
          under &&
          cursorOverlaySelectors.some((sel) => sel && under.closest(sel));
        if (!isOverOverlay) {
          cursor.classList.add("is-visible");
          gsap.set(cursor, cursorPos(this.mouseX, this.mouseY));
        }
        container.style.cursor = "none";
        fauxCursorListenersActive = true;
        document.addEventListener("pointermove", handleMouseMove);
      });

      container.addEventListener("pointerleave", (e) => {
        if (e.pointerType !== "mouse") return;
        if (!fauxCursorListenersActive) return;
        teardownFauxCursor();
      });
    }

    init() {
      if (this.isWhatWeDo || this.isOtherProjects) {
        this.syncWhatWeDoCarousel();
      } else if (this.isCustomTextImageSlider && this.stackOnMobile) {
        this.syncStackedMobileCarousel();
      } else {
        this.calculateBounds();
        this.createDraggable();
      }
    }
  }

  // Initialize on DOM ready, but wait for GSAP to be available
  function checkAndInit() {
    if (
      typeof gsap !== "undefined" &&
      typeof Draggable !== "undefined" &&
      typeof InertiaPlugin !== "undefined"
    ) {
      initCollections();
    } else {
      // Retry after a short delay if GSAP isn't loaded yet
      setTimeout(checkAndInit, 100);
    }
  }

  function bootCollections() {
    if (!isBlockOnPage(...COLLECTION_BLOCKS)) {
      return;
    }

    whenVisible(
      ".collections, .what-we-do, .other-projects, .team-slider, .gallery-slider, .custom-text-image-slider, .other-articles",
      checkAndInit,
      { rootMargin: "400px 0px" },
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootCollections);
  } else {
    bootCollections();
  }
})();
